import { lazy, Suspense, type ReactNode } from 'react';
import { LoadingState, Notice, uiStyles } from '../components';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { AuthScreen } from '../features/auth/AuthScreen';
import { AppShell } from './AppShell';
import { RailStatus } from './RailStatus';
import { RuntimeErrorBoundary } from './RuntimeErrorBoundary';
import { StudioProvider, useStudio } from './RepositoryContext';
import { SECTIONS, type SectionId } from './sections';
import { useSectionRoute } from './useSectionRoute';

/**
 * Feature routes are loaded only when selected. React's lazy API pairs with
 * the Suspense boundary below, while Vite turns each import into a build chunk.
 * Sources: https://react.dev/reference/react/lazy
 *          https://vite.dev/guide/features.html#dynamic-import
 */
const Dashboard = lazy(async () => {
  const route = await import('../features/dashboard/Dashboard');
  return { default: route.Dashboard };
});
const CultivarLibrary = lazy(async () => {
  const route = await import('../features/cultivars/CultivarLibrary');
  return { default: route.CultivarLibrary };
});
const Feed = lazy(async () => {
  const route = await import('../features/social/Feed');
  return { default: route.Feed };
});
const GrowThreadsSection = lazy(async () => {
  const route = await import('../features/social/GrowThreads');
  return { default: route.GrowThreadsSection };
});
const SocialProfileSection = lazy(async () => {
  const route = await import('../features/social/SocialProfile');
  return { default: route.SocialProfileSection };
});
const EquipmentSection = lazy(async () => {
  const route = await import('../features/equipment/EquipmentSection');
  return { default: route.EquipmentSection };
});
const KnowledgeSection = lazy(async () => {
  const route = await import('../features/knowledge/KnowledgeSection');
  return { default: route.KnowledgeSection };
});
const SpacesSection = lazy(async () => {
  const route = await import('../features/grow/GrowsSection');
  return { default: route.SpacesSection };
});
const MetricsSection = lazy(async () => {
  const route = await import('../features/metrics/MetricsSection');
  return { default: route.MetricsSection };
});
const RunsSection = lazy(async () => {
  const route = await import('../features/runs/RunsSection');
  return { default: route.RunsSection };
});
const ScenarioSectionRoute = lazy(async () => {
  const route = await import('./ScenarioSectionRoute');
  return { default: route.ScenarioSectionRoute };
});
const ProfileSettings = lazy(async () => {
  const route = await import('../features/settings/ProfileSettings');
  return { default: route.ProfileSettings };
});
const DesignLanguageGallery = lazy(async () => {
  const gallery = await import('../features/dev/DesignLanguageGallery');
  return { default: gallery.DesignLanguageGallery };
});

/** Placeholder for feature slices that land in later tasks. */
function SectionPlaceholder({ section }: { section: SectionId }): ReactNode {
  const entry = SECTIONS.find((candidate) => candidate.id === section);

  return (
    <section aria-labelledby="section-heading" className={uiStyles.stack}>
      <h2 id="section-heading">{entry?.label ?? section}</h2>
      <p className={uiStyles.hint}>{entry?.description}</p>
    </section>
  );
}

function SectionContent({
  section,
  navigate,
}: {
  section: SectionId;
  navigate: (next: SectionId) => void;
}): ReactNode {
  const { ready, error } = useStudio();

  if (error) {
    return (
      <Notice tone="danger" role="alert">
        GrowHub could not connect to your data, so nothing can be saved yet: {error}
      </Notice>
    );
  }

  if (!ready) {
    return <LoadingState label="Loading your GrowHub data" />;
  }

  switch (section) {
    case 'scenarios':
      return (
        <ScenarioSectionRoute
          onPlanned={() => {
            navigate('runs');
          }}
        />
      );
    case 'knowledge':
      return <KnowledgeSection />;
    case 'feed':
      return <Feed navigate={navigate} />;
    case 'threads':
      return <GrowThreadsSection />;
    case 'dashboard':
      return <Dashboard navigate={navigate} />;
    case 'spaces':
      return <SpacesSection />;
    case 'runs':
      return <RunsSection />;
    case 'cultivars':
      return <CultivarLibrary />;
    case 'equipment':
      return <EquipmentSection />;
    case 'metrics':
      return <MetricsSection />;
    case 'profile':
      return <SocialProfileSection />;
    case 'settings':
      return <ProfileSettings />;
    case 'ui-lab':
      return <DesignLanguageGallery />;
    default:
      return <SectionPlaceholder section={section} />;
  }
}

function AppRoutes(): ReactNode {
  const { section, navigate } = useSectionRoute();
  const { signOut } = useAuth();

  return (
    <AppShell
      section={section}
      onNavigate={navigate}
      onSignOut={signOut}
      railStatus={<RailStatus />}
    >
      <Suspense fallback={<LoadingState label={`Loading ${section}`} />}>
        <SectionContent section={section} navigate={navigate} />
      </Suspense>
    </AppShell>
  );
}

export function App(): ReactNode {
  return (
    <RuntimeErrorBoundary>
      <AuthProvider>
        <Root />
      </AuthProvider>
    </RuntimeErrorBoundary>
  );
}

function Root(): ReactNode {
  const { session, loading } = useAuth();

  if (loading) {
    return <LoadingState label="Checking your session" />;
  }

  if (!session) {
    return <AuthScreen />;
  }

  return (
    <StudioProvider>
      <AppRoutes />
    </StudioProvider>
  );
}
